import React from 'react';
import Grid from '@mui/material/Grid';
import Dialog from 'components/Dialog';
import ValidatedInput, {initialValue} from 'components/ValidatedInput';

import {APIError, apiCall} from 'functions/api';
import type {
  ClientResponse,
  InsertClientRequest,
  UpdateClientRequest,
} from 'types/clients';
import type {ListResponse} from 'types/utils';
import type {FormRole, InsertFormRequest, ListRolesRequest} from 'types/forms';

interface Props {
  open: boolean;
  onClose: (success: boolean) => void;
  client?: ClientResponse;
}
function ClientDialog(props: Props) {
  const [firstName, setFirstName] = React.useState(initialValue('text'));
  const [lastName, setLastName] = React.useState(initialValue('text'));
  const [email, setEmail] = React.useState(initialValue('email'));
  const [password, setPassword] = React.useState(initialValue('password'));
  const [repeatPassword, setRepeatPassword] = React.useState(
    initialValue('password'),
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState('');

  const error = React.useMemo(
    () =>
      firstName.state !== 'success' ||
      lastName.state !== 'success' ||
      email.state !== 'success' ||
      (!props.client &&
        (password.state !== 'success' ||
          repeatPassword.state !== 'success' ||
          password.value !== repeatPassword.value)),
    [firstName, lastName, email, password, repeatPassword],
  );

  const handleClose = () => {
    if (!submitting) {
      props.onClose(false);
    }
  };

  const handleSubmit = async () => {
    if (error) {
      return;
    }
    setSubmitting(true);
    setServerError('');
    const url = props.client
      ? `/location/clients/${props.client.id}`
      : '/location/clients';
    const request: InsertClientRequest | UpdateClientRequest = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      password: password.value,
    };
    if (props.client) {
      delete request.password;
    }
    try {
      const insertResponse: ClientResponse = await apiCall(
        url,
        'POST',
        request,
      );
      if (!props.client) {
        const request: ListRolesRequest = {
          autoAssign: true,
          portal: 'CLIENT',
        };
        const {data: forms}: ListResponse<FormRole> = await apiCall(
          '/location/formRoles',
          'GET',
          request,
        );
        await Promise.all(
          forms.map(role => {
            const request: InsertFormRequest = {
              type: 'INSTANCE',
              formId: role.formId,
              roles: [
                {
                  roleId: role.id,
                  userId: insertResponse.id,
                },
              ],
            };
            return apiCall('/location/forms', 'POST', request);
          }),
        );
      }
      setSubmitting(false);
      props.onClose(true);
    } catch (e) {
      setSubmitting(false);
      if (e instanceof APIError) {
        setServerError(
          e.message || 'An error occurred while submitting the form.',
        );
      }
    }
  };

  React.useEffect(() => {
    const {client} = props;
    if (props.open) {
      setFirstName(initialValue('text', client?.firstName));
      setLastName(initialValue('text', client?.lastName));
      setEmail(initialValue('email', client?.email));
      setPassword(initialValue('password'));
      setRepeatPassword(initialValue('password'));
    }
  }, [props.open, props.client]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={props.client ? 'Edit Client' : 'Create Client'}
      actions={[
        {
          text: 'Cancel',
          color: 'secondary',
          onClick: () => {
            handleClose();
          },
        },
        {
          text: submitting ? 'Submitting' : 'Submit',
          color: 'primary',
          submit: true,
          disabled: error || submitting,
        },
      ]}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <ValidatedInput
            label="First Name"
            value={firstName}
            onChange={setFirstName}
            type="text"
          />
        </Grid>
        <Grid item xs={6}>
          <ValidatedInput
            label="Last Name"
            value={lastName}
            onChange={setLastName}
            type="text"
          />
        </Grid>
      </Grid>
      <ValidatedInput
        label="Email"
        value={email}
        onChange={setEmail}
        type="email"
      />
      {props.client === undefined && (
        <>
          <ValidatedInput
            label="Password"
            value={password}
            onChange={setPassword}
            type="password"
          />
          <ValidatedInput
            label="Repeat Password"
            value={repeatPassword}
            onChange={setRepeatPassword}
            type="password"
          />
        </>
      )}
      {serverError && <p style={{color: 'red'}}>{serverError}</p>}
    </Dialog>
  );
}

export default ClientDialog;
